/**
 * Story 3.24 pass B — the ClickFunnels claim flow's Sentry signals, browser side.
 *
 * 🔴 WHY THIS EXISTS. Every failure in the post-purchase claim flow was SILENT:
 * the 3.24 cold review found them only by hand-reading Render request logs,
 * which expire after 14 days. These signals make the flow's failures land in
 * Sentry, filtered to `environment:production` (`TPH_Reference.md` §8).
 *
 * Shape: `log.reportSignal` — the one tph-api's custom alerts use
 * (`carrierVerdict.js`, `relay_video_host_stranded`): constant message, tags
 * `{ story: '3.24', stage: 'cf_claim_flow', signal }`, a per-signal fingerprint.
 *
 * ── SIGNAL `claim_ended_elsewhere` ──────────────────────────────────────────
 * A buyer who reached the claim page in THIS browser finished signing in
 * (password reset, `/login` or `/signup`, or the onboarding success button)
 * and was sent somewhere other than the wizard. Two reasons, both observable
 * only because the claim record is still in this browser:
 *   - `expired`           the record outlived its 60 minutes, so nothing could
 *                         route them back;
 *   - `routed_elsewhere`  the record was live but the sign-in followed router
 *                         state somewhere other than the claim — any page that
 *                         is not `/l/new?cf_order=<id>`, a bare `/l/new`
 *                         included (see `isClaimTarget`).
 * ⚠️ WHAT IT CANNOT SEE: a buyer who finished in a DIFFERENT browser or device
 * leaves no record here at all — that gap is accepted and ledgered (#1277).
 *
 * 🔴 PAYLOAD — exactly four fields, nothing else:
 *   `surface`      'password_reset' | 'sign_in' | 'onboarding'
 *   `reason`       'expired' | 'routed_elsewhere'
 *   `age_minutes`  whole minutes since the record was stamped
 *   `order_prefix` the FIRST THREE characters of the order id — enough to find
 *                  it in `cf_purchase_log` (`LIKE 'Abc%'`), not enough to
 *                  present it: the id is a bearer value (`logRedaction.js`).
 * Never the path, the full order id, an email address or a reset token. The
 * page URL and navigation breadcrumbs are query-stripped by `scrubEventUrls`,
 * so `/reset-password?t=…&e=…` and `?cf_order=…` never ride along either
 * (`cfClaimSignals.envelope.test.js` proves it on the transmitted envelope).
 */
import { reportSignal } from './log';
import { clearCfDestination, inspectCfDestination, readCfOrderFromSearch } from './cfOrder';

export const CF_CLAIM_STORY = '3.24';
export const CF_CLAIM_STAGE = 'cf_claim_flow';
export const SIGNAL_CLAIM_ENDED_ELSEWHERE = 'claim_ended_elsewhere';

const CLAIM_ENDED_ELSEWHERE_MESSAGE =
  'cf claim: a buyer who reached the claim page finished signing in somewhere other than the wizard';

const ORDER_PREFIX_LENGTH = 3;
const CLAIM_PATH = '/l/new';

/** The first three characters of the order id in `path`, or null. */
export const orderPrefixOf = path => {
  if (typeof path !== 'string') {
    return null;
  }
  // The hash is not part of the query: `?cf_order=Abc123#x` must read `Abc123`.
  const withoutHash = path.split('#')[0];
  const queryIndex = withoutHash.indexOf('?');
  const orderId = queryIndex === -1 ? null : readCfOrderFromSearch(withoutHash.slice(queryIndex));
  return orderId ? orderId.slice(0, ORDER_PREFIX_LENGTH) : null;
};

/**
 * True when `target` is the CLAIM: exactly `/l/new`, carrying a well-formed
 * `cf_order` in its query. That is the only shape that presents an order —
 * Story 3.19's authenticated-arrival consumer fires the attach for the id in
 * that URL and nothing else.
 *
 * 🔴 Pass C (ruling on finding 7) — TWO DECISIONS, BOTH PINNED BY TEST:
 *   - the path is matched by EQUALITY, never by prefix: `/l/<slug>/<id>`, the
 *     draft wizard `/l/draft/…/new/details` and `/l/newest` are all somewhere
 *     else. A `startsWith('/l/')` matcher passed every pass-B test.
 *   - a BARE `/l/new` does NOT count. It opens the wizard, but with no order in
 *     the URL no attach fires, so a buyer whose account email differs from the
 *     payment email lands in a wizard with nothing attached — exactly the
 *     failure this signal names. A live claim record bypassed to a bare
 *     `/l/new` is reported as `routed_elsewhere`; an expired one as `expired`.
 */
export const isClaimTarget = target =>
  typeof target === 'string' &&
  target.split(/[?#]/)[0] === CLAIM_PATH &&
  orderPrefixOf(target) !== null;

/**
 * The payload — built in ONE place so the four-field rule has one home.
 *
 * @returns {{surface: string, reason: string, age_minutes: (number|null), order_prefix: (string|null)}}
 */
export const claimEndedElsewhereExtra = ({ surface, reason, record, now = Date.now() }) => ({
  surface,
  reason,
  age_minutes:
    typeof record?.storedAt === 'number' ? Math.floor((now - record.storedAt) / 60000) : null,
  order_prefix: orderPrefixOf(record?.path),
});

// At most one report per record per surface per page session. Sentry's Dedupe
// integration would drop an identical consecutive message anyway; this keeps
// a re-render or a repeated effect from even trying.
const reportedKeys = new Set();

/**
 * The one decision every sign-in-finishing surface makes. `target` is where
 * the page is about to send the user (null when it has no destination of its
 * own — the landing page or the verification screen).
 *
 * @returns {string|null} the reason reported, or null when nothing was
 */
export const noteClaimOutcome = ({ surface, target = null }) => {
  try {
    const record = inspectCfDestination();
    if (isClaimTarget(target)) {
      // Going to the claim anyway: nothing to report. An expired record is
      // still dropped, so a LATER sign-in cannot report it.
      if (record.status === 'expired') {
        clearCfDestination();
      }
      return null;
    }
    const reason =
      record.status === 'expired'
        ? 'expired'
        : record.status === 'live' && target
        ? 'routed_elsewhere'
        : null;
    if (!reason) {
      return null;
    }
    if (reason === 'expired') {
      // Reported once, then forgotten: an expired record has done its job.
      clearCfDestination();
    }
    const key = `${surface}|${record.storedAt}`;
    if (reportedKeys.has(key)) {
      return reason;
    }
    reportedKeys.add(key);
    reportSignal({
      story: CF_CLAIM_STORY,
      stage: CF_CLAIM_STAGE,
      signal: SIGNAL_CLAIM_ENDED_ELSEWHERE,
      message: CLAIM_ENDED_ELSEWHERE_MESSAGE,
      extra: claimEndedElsewhereExtra({ surface, reason, record }),
    });
    return reason;
  } catch (e) {
    // FAIL-OPEN: reporting must never break a sign-in.
    return null;
  }
};

/** Test seam: the per-session de-duplication set is module state. */
export const __resetClaimSignalsForTests = () => reportedKeys.clear();
